import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
  LockOpenIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { PMDialog } from "../../../components/utils/PMDialog";
import { useCanManageHoursMonthlyOverview } from "../../../routes/roleOnly";
import {
  createOrRefreshPayrollBasis,
  fetchPayrollBasis,
  fetchPayrollBases,
  fetchPayrollBasisExportStatus,
  finalizePayrollBasis,
  getPayrollBasisErrorMessage,
  isPayrollBasisUnavailableStatus,
  reopenPayrollBasis,
  startPayrollBasisExport,
  triggerBrowserDownload,
  updatePayrollBasisEmployeeNote,
  updatePayrollBasisNotes,
} from "../../../api/payrollBasis";
import {
  PayrollBasisEmployeeNoteCell,
  PayrollBasisGeneralNotes,
  resolveBasisNotes,
  resolveEmployeeNote,
} from "../../../components/dashboard/hours/payroll_basis_notes";

const ACCENT = {
  solid: "#1463C7",
  soft: "#EAF3FD",
  deep: "#0B3A78",
  mid: "#2D8BE8",
  heroBg: "linear-gradient(160deg,#EAF3FD,#F2F8FE)",
  heroBorder: "#BFDCF7",
};

const toYmd = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const monthBounds = (date = new Date()) => {
  const from = new Date(date.getFullYear(), date.getMonth(), 1);
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    periodType: "month",
    from: toYmd(from),
    to: toYmd(to),
  };
};

const yearMonthFromPeriod = (period) => {
  if (!period?.from) return null;
  const [y, m] = String(period.from).split("-");
  const year = Number(y);
  const month = Number(m);
  if (!year || !month) return null;
  return { year, month };
};

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}h`;
};

const employeeName = (row) =>
  row?.user?.displayTitle || row?.user?.name || row?.user?.email || "—";

const employeeDepartment = (row) => {
  const dept = row?.user?.department;
  if (!dept) return "—";
  if (typeof dept === "string") return dept;
  return dept.displayTitle || dept.title || dept.name || "—";
};

const statusTone = (status) => {
  switch (status) {
    case "finalized":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "exported":
      return "bg-blue-50 text-blue-800 ring-blue-200";
    case "draft":
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
};

const EXPORT_DONE = new Set(["completed", "complete", "ready", "done", "success"]);
const EXPORT_FAIL = new Set(["failed", "error"]);
const POLL_MS = 2000;
const POLL_MAX_ATTEMPTS = 90;

const PayrollErrorBanner = ({ title, message, onDismiss, dismissLabel }) => {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 shadow-[0_1px_2px_rgba(220,38,38,.08)]"
    >
      <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold text-red-950">{title}</p> : null}
        <p className={title ? "mt-1 text-red-900/95" : "font-medium text-red-950"}>
          {message}
        </p>
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 text-red-700 hover:bg-red-100 hover:text-red-900"
          aria-label={dismissLabel}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      ) : null}
    </div>
  );
};

export const HoursPayrollBasis = () => {
  const { t, i18n } = useTranslation();
  const canManage = useCanManageHoursMonthlyOverview();
  const [period, setPeriod] = useState(() => monthBounds());
  const [basis, setBasis] = useState(null);
  const [history, setHistory] = useState([]);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportState, setExportState] = useState({
    active: false,
    jobId: null,
    status: null,
    message: null,
  });
  const [noteDrafts, setNoteDrafts] = useState({});
  const [generalNotesDraft, setGeneralNotesDraft] = useState("");
  const [savingNoteUserId, setSavingNoteUserId] = useState(null);
  const [savingGeneralNotes, setSavingGeneralNotes] = useState(false);
  const pollRef = useRef(null);
  const loadSeq = useRef(0);

  const clearPageError = useCallback(() => setPageError(null), []);

  const showPageError = useCallback(
    (message, { title, toast: withToast = true } = {}) => {
      const msg =
        (typeof message === "string" && message.trim()) ||
        t("hours.payroll_basis.error_generic");
      setApiUnavailable(false);
      setPageError({
        title: title || t("hours.payroll_basis.error_title"),
        message: msg,
      });
      if (withToast) toast.error(msg);
    },
    [t]
  );

  const markApiUnavailable = useCallback(
    ({ toast: withToast = true } = {}) => {
      setApiUnavailable(true);
      setPageError(null);
      if (withToast) toast.error(t("hours.payroll_basis.api_unavailable_toast"));
    },
    [t]
  );

  const ym = useMemo(() => yearMonthFromPeriod(period), [period]);

  const periodLabel = useMemo(() => {
    if (!period?.from) return "";
    const date = new Date(`${period.from}T12:00:00`);
    try {
      return new Intl.DateTimeFormat(i18n.language || "nb", {
        month: "long",
        year: "numeric",
      }).format(date);
    } catch {
      return period.from;
    }
  }, [period?.from, i18n.language]);

  const employees = useMemo(
    () => (Array.isArray(basis?.employees) ? basis.employees : []),
    [basis]
  );

  const totals = useMemo(() => {
    return employees.reduce(
      (acc, row) => {
        acc.approved += Number(row.cached_approved_hours) || 0;
        acc.unapproved += Number(row.cached_unapproved_hours) || 0;
        acc.overtime += Number(row.cached_overtime_hours) || 0;
        acc.absence += Number(row.cached_absence_hours) || 0;
        acc.withDocs += row.has_documentation ? 1 : 0;
        acc.withUnapproved += row.has_unapproved ? 1 : 0;
        return acc;
      },
      {
        approved: 0,
        unapproved: 0,
        overtime: 0,
        absence: 0,
        withDocs: 0,
        withUnapproved: 0,
      }
    );
  }, [employees]);

  const editable = basis?.status === "draft";
  const isLocked = Boolean(basis && !editable);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const loadHistory = useCallback(async () => {
    if (!canManage) return;
    try {
      const result = await fetchPayrollBases();
      if (result.unavailable) return;
      const rows = Array.isArray(result.data) ? result.data : [];
      setHistory(rows);
    } catch {
      // History is secondary — ignore failures.
    }
  }, [canManage]);

  const applyBasisData = useCallback((data) => {
    setBasis(data);
    setGeneralNotesDraft(resolveBasisNotes(data));
    const drafts = {};
    (data?.employees || []).forEach((row) => {
      drafts[row.user_id] = resolveEmployeeNote(row);
    });
    setNoteDrafts(drafts);
  }, []);

  const loadBasis = useCallback(async () => {
    if (!canManage || !ym) {
      setIsLoading(false);
      return;
    }

    const seq = ++loadSeq.current;
    setIsLoading(true);
    setPageError(null);

    try {
      // GET by period — works for draft, finalized and exported (no create/refresh).
      const result = await fetchPayrollBases({ year: ym.year, month: ym.month });
      if (seq !== loadSeq.current) return;

      if (result.unavailable) {
        setApiUnavailable(true);
        setBasis(null);
        setNoteDrafts({});
        setGeneralNotesDraft("");
        return;
      }

      setApiUnavailable(false);
      const match =
        (result.data || []).find(
          (row) => Number(row.year) === ym.year && Number(row.month) === ym.month
        ) || null;

      if (match?.id) {
        // Index omits employees — hydrate via show (allowed for all statuses).
        const full = await fetchPayrollBasis(match.id);
        if (seq !== loadSeq.current) return;
        if (full.unavailable) {
          setApiUnavailable(true);
          setBasis(null);
          return;
        }
        if (full.notFound || !full.data) {
          // Show failed but index found the period — still open summary read-only.
          applyBasisData({ ...match, employees: match.employees || [] });
          return;
        }
        applyBasisData(full.data);
      } else {
        setBasis(null);
        setNoteDrafts({});
        setGeneralNotesDraft("");
      }
    } catch (error) {
      if (seq !== loadSeq.current) return;
      setBasis(null);
      setNoteDrafts({});
      setGeneralNotesDraft("");
      setApiUnavailable(false);
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        setApiUnavailable(true);
        return;
      }
      const fallback =
        error?.message === "forbidden" || error?.response?.status === 403
          ? t("hours.payroll_basis.error_forbidden")
          : t("hours.payroll_basis.error_load");
      const msg = getPayrollBasisErrorMessage(error, fallback);
      setPageError({
        title: t("hours.payroll_basis.error_load_title"),
        message: msg,
      });
      toast.error(msg);
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, [applyBasisData, canManage, ym, t]);

  useEffect(() => {
    loadBasis();
  }, [loadBasis]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const selectHistoryPeriod = useCallback((row) => {
    if (!row?.year || !row?.month) return;
    const from = new Date(Number(row.year), Number(row.month) - 1, 1);
    const to = new Date(Number(row.year), Number(row.month), 0);
    setPeriod({
      periodType: "month",
      from: toYmd(from),
      to: toYmd(to),
    });
  }, []);

  const handleRefresh = async () => {
    if (!ym || isRefreshing || isLocked) return;
    setIsRefreshing(true);
    clearPageError();
    try {
      const result = await createOrRefreshPayrollBasis({
        year: ym.year,
        month: ym.month,
      });

      if (result.unavailable) {
        markApiUnavailable();
        return;
      }

      if (result.status === 422 || (result.status >= 400 && result.status < 600)) {
        const msg = getPayrollBasisErrorMessage(
          result,
          t("hours.payroll_basis.error_refresh_locked")
        );
        showPageError(msg, { title: t("hours.payroll_basis.error_refresh") });
        return;
      }

      const data = result.data;
      if (!data) {
        showPageError(t("hours.payroll_basis.error_refresh"), {
          title: t("hours.payroll_basis.error_refresh"),
        });
        return;
      }

      setApiUnavailable(false);
      applyBasisData(data);
      loadHistory();
      toast.success(
        result.status === 201
          ? t("hours.payroll_basis.toast_created")
          : t("hours.payroll_basis.toast_refreshed")
      );
    } catch (error) {
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        markApiUnavailable();
        return;
      }
      const fallback =
        error?.message === "forbidden" || error?.response?.status === 403
          ? t("hours.payroll_basis.error_forbidden")
          : t("hours.payroll_basis.error_refresh");
      showPageError(getPayrollBasisErrorMessage(error, fallback), {
        title: t("hours.payroll_basis.error_refresh"),
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSaveNote = async (row, htmlOverride) => {
    if (!basis?.id || !editable) return;
    const next =
      htmlOverride != null ? htmlOverride : noteDrafts[row.user_id] ?? "";
    const previous = resolveEmployeeNote(row);
    if (previous === next) return;

    setSavingNoteUserId(row.user_id);
    clearPageError();
    try {
      const result = await updatePayrollBasisEmployeeNote({
        payrollBasisId: basis.id,
        userId: row.user_id,
        manualNote: next,
      });
      if (result.unavailable) {
        markApiUnavailable();
        return;
      }
      const updated = result.data;
      const saved = resolveEmployeeNote(updated) || next;
      setNoteDrafts((prev) => ({ ...prev, [row.user_id]: saved }));
      setBasis((prev) => {
        if (!prev?.employees) return prev;
        return {
          ...prev,
          employees: prev.employees.map((emp) =>
            emp.user_id === row.user_id
              ? { ...emp, manual_note: saved, notes: saved }
              : emp
          ),
        };
      });
      toast.success(t("hours.payroll_basis.toast_note_saved"));
    } catch (error) {
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        markApiUnavailable();
        return;
      }
      showPageError(
        getPayrollBasisErrorMessage(error, t("hours.payroll_basis.error_note")),
        { title: t("hours.payroll_basis.error_note") }
      );
    } finally {
      setSavingNoteUserId(null);
    }
  };

  const handleSaveGeneralNotes = async () => {
    if (!basis?.id || !editable || !ym || savingGeneralNotes) return;
    const next = generalNotesDraft ?? "";
    if (resolveBasisNotes(basis) === next) {
      toast.success(t("hours.payroll_basis.toast_note_saved"));
      return;
    }

    setSavingGeneralNotes(true);
    clearPageError();
    try {
      const result = await updatePayrollBasisNotes({
        payrollBasisId: basis.id,
        notes: next,
        year: ym.year,
        month: ym.month,
      });
      if (result.unavailable) {
        markApiUnavailable();
        return;
      }
      const data = result.data;
      // POST fallback refreshes the whole basis; PATCH may return only notes.
      if (data?.employees || data?.id === basis.id) {
        setBasis((prev) => ({
          ...prev,
          ...data,
          notes: resolveBasisNotes(data) || next,
          employees: data.employees || prev?.employees,
        }));
        if (Array.isArray(data.employees)) {
          const drafts = {};
          data.employees.forEach((row) => {
            drafts[row.user_id] = resolveEmployeeNote(row);
          });
          setNoteDrafts(drafts);
        }
      } else {
        setBasis((prev) =>
          prev
            ? {
                ...prev,
                notes: resolveBasisNotes(data) || next,
              }
            : prev
        );
      }
      setGeneralNotesDraft(resolveBasisNotes(data) || next);
      toast.success(t("hours.payroll_basis.toast_general_notes_saved"));
    } catch (error) {
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        markApiUnavailable();
        return;
      }
      showPageError(
        getPayrollBasisErrorMessage(
          error,
          t("hours.payroll_basis.error_general_notes")
        ),
        { title: t("hours.payroll_basis.error_general_notes") }
      );
    } finally {
      setSavingGeneralNotes(false);
    }
  };

  const handleFinalize = async () => {
    if (!basis?.id || !editable || isFinalizing) return;
    setIsFinalizing(true);
    clearPageError();
    try {
      const result = await finalizePayrollBasis(basis.id);
      if (result.unavailable) {
        markApiUnavailable();
        return;
      }
      if (!result.data) {
        showPageError(t("hours.payroll_basis.error_finalize"), {
          title: t("hours.payroll_basis.error_finalize"),
        });
        return;
      }
      applyBasisData(result.data);
      loadHistory();
      toast.success(t("hours.payroll_basis.toast_finalized"));
    } catch (error) {
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        markApiUnavailable();
        return;
      }
      showPageError(
        getPayrollBasisErrorMessage(
          error,
          t("hours.payroll_basis.error_finalize")
        ),
        { title: t("hours.payroll_basis.error_finalize") }
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleReopen = async () => {
    if (!basis?.id || !isLocked || isReopening) return;
    setIsReopening(true);
    clearPageError();
    try {
      const result = await reopenPayrollBasis(basis.id);
      if (result.unavailable) {
        markApiUnavailable();
        return;
      }
      if (!result.data) {
        showPageError(t("hours.payroll_basis.error_reopen"), {
          title: t("hours.payroll_basis.error_reopen"),
        });
        return;
      }
      setReopenDialogOpen(false);
      applyBasisData(result.data);
      loadHistory();
      toast.success(t("hours.payroll_basis.toast_reopened"));
    } catch (error) {
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        markApiUnavailable();
        return;
      }
      showPageError(
        getPayrollBasisErrorMessage(
          error,
          t("hours.payroll_basis.error_reopen")
        ),
        { title: t("hours.payroll_basis.error_reopen") }
      );
    } finally {
      setIsReopening(false);
    }
  };

  const pollExport = useCallback(
    async (payrollBasisId, jobId, attempt = 0) => {
      stopPolling();
      if (attempt >= POLL_MAX_ATTEMPTS) {
        const msg = t("hours.payroll_basis.export_timeout");
        setExportState({
          active: false,
          jobId,
          status: "timeout",
          message: msg,
        });
        showPageError(msg, { title: t("hours.payroll_basis.export_failed") });
        return;
      }

      try {
        const result = await fetchPayrollBasisExportStatus(payrollBasisId, jobId);
        if (result.unavailable) {
          setExportState({
            active: false,
            jobId,
            status: "unavailable",
            message: t("hours.payroll_basis.api_unavailable_toast"),
          });
          markApiUnavailable();
          return;
        }

        const status = String(result.data?.status || "").toLowerCase();
        const metadata = result.data?.metadata || {};

        if (EXPORT_DONE.has(status)) {
          const zipUrl = metadata.zip_url || metadata.download_url || metadata.url;
          const filename =
            metadata.filename ||
            `lonnsgrunnlag-${ym?.year || ""}-${String(ym?.month || "").padStart(2, "0")}.zip`;
          setExportState({
            active: false,
            jobId,
            status: "completed",
            message: null,
          });
          clearPageError();
          if (zipUrl) {
            triggerBrowserDownload(zipUrl, filename);
            toast.success(t("hours.payroll_basis.export_ready"));
          } else {
            toast.success(t("hours.payroll_basis.export_done_no_url"));
          }
          // Reload basis to pick up exported status
          loadBasis();
          loadHistory();
          return;
        }

        if (EXPORT_FAIL.has(status)) {
          const msg =
            metadata.error ||
            getPayrollBasisErrorMessage(
              result,
              t("hours.payroll_basis.export_failed")
            );
          setExportState({
            active: false,
            jobId,
            status: "failed",
            message: msg,
          });
          showPageError(msg, { title: t("hours.payroll_basis.export_failed") });
          return;
        }

        setExportState({
          active: true,
          jobId,
          status: status || "processing",
          message: null,
        });
        pollRef.current = setTimeout(
          () => pollExport(payrollBasisId, jobId, attempt + 1),
          POLL_MS
        );
      } catch (error) {
        if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
          setExportState({
            active: false,
            jobId,
            status: "unavailable",
            message: t("hours.payroll_basis.api_unavailable_toast"),
          });
          markApiUnavailable();
          return;
        }
        const msg = getPayrollBasisErrorMessage(
          error,
          t("hours.payroll_basis.export_failed")
        );
        setExportState({
          active: false,
          jobId,
          status: "failed",
          message: msg,
        });
        showPageError(msg, { title: t("hours.payroll_basis.export_failed") });
      }
    },
    [
      clearPageError,
      loadBasis,
      loadHistory,
      markApiUnavailable,
      showPageError,
      stopPolling,
      t,
      ym,
    ]
  );

  const runExport = async (mode) => {
    if (!basis?.id || exportState.active) return;
    clearPageError();
    setExportDialogOpen(false);
    setExportState({ active: true, jobId: null, status: "queued", message: null });
    try {
      const result = await startPayrollBasisExport(basis.id, { mode });
      if (result.unavailable) {
        setExportState({
          active: false,
          jobId: null,
          status: "unavailable",
          message: t("hours.payroll_basis.api_unavailable_toast"),
        });
        markApiUnavailable();
        return;
      }
      if (result.status === 422) {
        const msg = getPayrollBasisErrorMessage(
          result,
          t("hours.payroll_basis.export_failed")
        );
        setExportState({
          active: false,
          jobId: null,
          status: "failed",
          message: msg,
        });
        showPageError(msg, { title: t("hours.payroll_basis.export_failed") });
        return;
      }
      const jobId = result.data?.job_id;
      if (!jobId) {
        const msg = getPayrollBasisErrorMessage(
          result,
          t("hours.payroll_basis.export_failed")
        );
        setExportState({
          active: false,
          jobId: null,
          status: "failed",
          message: msg,
        });
        showPageError(msg, { title: t("hours.payroll_basis.export_failed") });
        return;
      }
      // Optimistic UI when finalize_and_export locked the period.
      if (mode === "finalize_and_export" && basis?.status === "draft") {
        setBasis((prev) => (prev ? { ...prev, status: "finalized" } : prev));
      }
      setExportState({ active: true, jobId, status: "queued", message: null });
      toast.success(t("hours.payroll_basis.export_started"));
      pollExport(basis.id, jobId, 0);
    } catch (error) {
      if (isPayrollBasisUnavailableStatus(error?.response?.status)) {
        setExportState({
          active: false,
          jobId: null,
          status: "unavailable",
          message: t("hours.payroll_basis.api_unavailable_toast"),
        });
        markApiUnavailable();
        return;
      }
      const msg = getPayrollBasisErrorMessage(
        error,
        t("hours.payroll_basis.export_failed")
      );
      setExportState({
        active: false,
        jobId: null,
        status: "failed",
        message: msg,
      });
      showPageError(msg, { title: t("hours.payroll_basis.export_failed") });
    }
  };

  const handleExportClick = () => {
    if (!basis?.id || exportState.active) return;
    if (basis.status === "draft") {
      setExportDialogOpen(true);
      return;
    }
    runExport(undefined);
  };

  if (!canManage) {
    return <Navigate to="/dashboard/hours" replace />;
  }

  const statusLabel = basis?.status
    ? t(`hours.payroll_basis.status.${basis.status}`, {
        defaultValue: basis.status,
      })
    : null;

  return (
    <DashboardHeader
      title={t("hours.payroll_basis.title")}
      subtitle={t("hours.payroll_basis.subtitle")}
    >
      <div className="space-y-5 px-3 py-3 sm:px-4 sm:py-4">
        {/* Toolbar */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0 flex-1">
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                {t("hours.payroll_basis.period")}
              </label>
              <PeriodSelector
                periodType={period.periodType}
                from={period.from}
                to={period.to}
                allowedPeriodTypes={["month"]}
                onChange={(next) =>
                  setPeriod({
                    periodType: next?.periodType || "month",
                    from: next?.from,
                    to: next?.to,
                  })
                }
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {!isLocked ? (
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing || !ym || apiUnavailable}
                  className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.55)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                  style={
                    isRefreshing || !ym || apiUnavailable
                      ? undefined
                      : { background: "linear-gradient(135deg,#1463C7,#2D8BE8)" }
                  }
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  {basis
                    ? t("hours.payroll_basis.action_refresh")
                    : t("hours.payroll_basis.action_create")}
                </button>
              ) : null}

              {!isLocked ? (
                <button
                  type="button"
                  onClick={handleFinalize}
                  disabled={!basis || !editable || isFinalizing || apiUnavailable}
                  className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  <LockClosedIcon className="h-4 w-4" />
                  {t("hours.payroll_basis.action_finalize")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setReopenDialogOpen(true)}
                  disabled={!basis?.id || isReopening || apiUnavailable}
                  className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                >
                  <LockOpenIcon className="h-4 w-4" />
                  {t("hours.payroll_basis.action_reopen")}
                </button>
              )}

              <button
                type="button"
                onClick={handleExportClick}
                disabled={!basis?.id || exportState.active || apiUnavailable}
                className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                <DocumentArrowDownIcon
                  className={`h-4 w-4 ${exportState.active ? "animate-pulse" : ""}`}
                />
                {exportState.active
                  ? t("hours.payroll_basis.export_running")
                  : isLocked
                    ? t("hours.payroll_basis.export_redownload")
                    : t("hours.payroll_basis.export_zip")}
              </button>
            </div>
          </div>

          {basis ? (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${statusTone(
                  basis.status
                )}`}
              >
                {statusLabel}
              </span>
              {periodLabel ? (
                <span className="text-slate-500">
                  {t("hours.payroll_basis.table_subtitle", { period: periodLabel })}
                </span>
              ) : null}
              {exportState.active ? (
                <span className="text-[#1463C7]">
                  {t("hours.payroll_basis.export_status", {
                    status: exportState.status || "…",
                  })}
                </span>
              ) : null}
            </div>
          ) : null}

          {history.length > 0 ? (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("hours.payroll_basis.history_title")}
              </p>
              <div className="flex flex-wrap gap-2">
                {history.map((row) => {
                  const active =
                    ym &&
                    Number(row.year) === ym.year &&
                    Number(row.month) === ym.month;
                  const label = `${String(row.month).padStart(2, "0")}.${row.year}`;
                  const rowStatus = t(`hours.payroll_basis.status.${row.status}`, {
                    defaultValue: row.status,
                  });
                  return (
                    <button
                      key={row.id || `${row.year}-${row.month}`}
                      type="button"
                      onClick={() => selectHistoryPeriod(row)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
                        active
                          ? "border-[#1463C7] bg-[#EAF3FD] text-[#0B3A78]"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span>{label}</span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${statusTone(
                          row.status
                        )}`}
                      >
                        {rowStatus}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        {apiUnavailable ? (
          <div
            role="status"
            className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">
                {t("hours.payroll_basis.api_unavailable_title")}
              </p>
              <p className="mt-1 text-amber-900/90">
                {t("hours.payroll_basis.api_unavailable_body")}
              </p>
            </div>
          </div>
        ) : null}

        {!apiUnavailable && pageError ? (
          <PayrollErrorBanner
            title={pageError.title}
            message={pageError.message}
            onDismiss={clearPageError}
            dismissLabel={t("hours.payroll_basis.error_dismiss")}
          />
        ) : null}

        {!apiUnavailable &&
        !pageError &&
        exportState.message &&
        !exportState.active &&
        (exportState.status === "failed" || exportState.status === "timeout") ? (
          <PayrollErrorBanner
            title={t("hours.payroll_basis.export_failed")}
            message={exportState.message}
            onDismiss={() =>
              setExportState((prev) => ({ ...prev, message: null, status: null }))
            }
            dismissLabel={t("hours.payroll_basis.error_dismiss")}
          />
        ) : null}

        {isLocked && !apiUnavailable ? (
          <div
            role="status"
            className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="flex gap-3">
              <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
              <div>
                <p className="font-semibold">
                  {t("hours.payroll_basis.read_only_title")}
                </p>
                <p className="mt-1 text-slate-600">
                  {t("hours.payroll_basis.read_only_body")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setReopenDialogOpen(true)}
              disabled={isReopening || apiUnavailable}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
              {t("hours.payroll_basis.action_reopen")}
            </button>
          </div>
        ) : null}

        {!apiUnavailable && !basis && !isLoading && !pageError ? (
          <div
            className="flex gap-3 rounded-xl border px-4 py-3 text-sm"
            style={{
              borderColor: ACCENT.heroBorder,
              background: ACCENT.heroBg,
              color: ACCENT.deep,
            }}
          >
            <InformationCircleIcon
              className="mt-0.5 h-5 w-5 shrink-0"
              style={{ color: ACCENT.solid }}
            />
            <div>
              <p className="font-semibold">
                {t("hours.payroll_basis.empty_draft_title")}
              </p>
              <p className="mt-1 opacity-90">
                {t("hours.payroll_basis.empty_draft_body")}
              </p>
            </div>
          </div>
        ) : null}

        {/* Generelle notater (hele perioden) */}
        {basis && !apiUnavailable ? (
          <PayrollBasisGeneralNotes
            value={generalNotesDraft}
            editable={editable}
            saving={savingGeneralNotes}
            onChange={setGeneralNotesDraft}
            onSave={handleSaveGeneralNotes}
          />
        ) : null}

        {/* Summary cards — approved totals only */}
        {basis && !apiUnavailable ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              {
                key: "approved",
                label: t("hours.payroll_basis.summary_approved"),
                value: formatHours(totals.approved),
              },
              {
                key: "ot",
                label: t("hours.payroll_basis.summary_overtime"),
                value: formatHours(totals.overtime),
              },
              {
                key: "absence",
                label: t("hours.payroll_basis.summary_absence"),
                value: formatHours(totals.absence),
              },
              {
                key: "unapproved",
                label: t("hours.payroll_basis.summary_unapproved"),
                value: formatHours(totals.unapproved),
                warn: totals.unapproved > 0,
              },
            ].map((card) => (
              <div
                key={card.key}
                className={`rounded-xl border bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,.04)] ${
                  card.warn
                    ? "border-red-200 bg-red-50/40"
                    : "border-slate-200"
                }`}
              >
                <div
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    card.warn ? "text-red-700" : "text-slate-500"
                  }`}
                >
                  {card.label}
                </div>
                <div
                  className={`mt-1 text-xl font-bold tabular-nums ${
                    card.warn ? "text-red-700" : "text-slate-900"
                  }`}
                >
                  {card.value}
                </div>
                {card.warn ? (
                  <p className="mt-1 text-xs text-red-600">
                    {t("hours.payroll_basis.summary_unapproved_hint")}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {/* Employee table */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
          <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-4">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl"
              style={{ background: ACCENT.soft, color: ACCENT.solid }}
            >
              <BanknotesIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900">
                {t("hours.payroll_basis.table_title")}
              </h2>
              <p className="text-sm text-slate-500">
                {periodLabel
                  ? t("hours.payroll_basis.table_subtitle", { period: periodLabel })
                  : t("hours.payroll_basis.table_subtitle_empty")}
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="px-4 py-10">
              <ResourceLoading />
            </div>
          ) : pageError && !basis ? (
            <div className="px-4 py-12 text-center">
              <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-red-500" />
              <p className="mt-3 font-semibold text-red-900">
                {pageError.title || t("hours.payroll_basis.error_title")}
              </p>
              <p className="mt-1 text-sm text-red-800/90">{pageError.message}</p>
              <button
                type="button"
                onClick={() => loadBasis()}
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-800 hover:bg-red-50"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t("hours.payroll_basis.error_retry")}
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 whitespace-nowrap">
                        {t("hours.payroll_basis.col_employee")}
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap">
                        {t("hours.payroll_basis.col_department")}
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-right">
                        {t("hours.payroll_basis.col_approved")}
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-right">
                        {t("hours.payroll_basis.col_unapproved")}
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-right">
                        {t("hours.payroll_basis.col_overtime")}
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-right">
                        {t("hours.payroll_basis.col_absence")}
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap">
                        {t("hours.payroll_basis.col_docs")}
                      </th>
                      <th className="min-w-[260px] px-4 py-3">
                        {t("hours.payroll_basis.col_note")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {!basis || employees.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-12 text-center text-slate-500"
                        >
                          <p className="font-medium text-slate-700">
                            {t("hours.payroll_basis.empty_title")}
                          </p>
                          <p className="mt-1 text-sm">
                            {apiUnavailable
                              ? t("hours.payroll_basis.api_unavailable_body")
                              : t("hours.payroll_basis.empty_body")}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      employees.map((row) => {
                        const hasUnapproved = Boolean(row.has_unapproved);
                        return (
                          <tr
                            key={row.id || row.user_id}
                            className={
                              hasUnapproved
                                ? "bg-red-50/50 hover:bg-red-50"
                                : "hover:bg-slate-50/80"
                            }
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">
                                {employeeName(row)}
                              </div>
                              {hasUnapproved ? (
                                <div className="mt-0.5 text-xs font-medium text-red-600">
                                  {t("hours.payroll_basis.unapproved_badge", {
                                    count: row.unapproved_count || 0,
                                  })}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {employeeDepartment(row)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                              {formatHours(row.cached_approved_hours)}
                            </td>
                            <td
                              className={`px-4 py-3 text-right font-semibold tabular-nums ${
                                hasUnapproved ? "text-red-600" : "text-slate-400"
                              }`}
                            >
                              {formatHours(row.cached_unapproved_hours)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                              {formatHours(row.cached_overtime_hours)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                              {formatHours(row.cached_absence_hours)}
                            </td>
                            <td className="px-4 py-3">
                              {row.has_documentation ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  <DocumentTextIcon className="h-3.5 w-3.5" />
                                  {t("hours.payroll_basis.docs_yes")}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-xs font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
                                  {t("hours.payroll_basis.docs_no")}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 align-top">
                              <PayrollBasisEmployeeNoteCell
                                row={row}
                                value={noteDrafts[row.user_id] ?? ""}
                                editable={editable}
                                saving={savingNoteUserId === row.user_id}
                                onChange={(html) =>
                                  setNoteDrafts((prev) => ({
                                    ...prev,
                                    [row.user_id]: html,
                                  }))
                                }
                                onSave={(html) => handleSaveNote(row, html)}
                              />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {employees.length > 0 ? (
                    <tfoot className="border-t border-slate-200 bg-slate-50 text-sm font-semibold">
                      <tr>
                        <td className="px-4 py-3" colSpan={2}>
                          {t("hours.payroll_basis.footer_totals")}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatHours(totals.approved)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-red-600">
                          {formatHours(totals.unapproved)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatHours(totals.overtime)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatHours(totals.absence)}
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-slate-500" colSpan={2}>
                          {t("hours.payroll_basis.footer_docs", {
                            count: totals.withDocs,
                          })}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-3 p-3 md:hidden">
                {!basis || employees.length === 0 ? (
                  <div className="rounded-lg bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                    <p className="font-medium text-slate-700">
                      {apiUnavailable
                        ? t("hours.payroll_basis.api_unavailable_title")
                        : t("hours.payroll_basis.empty_title")}
                    </p>
                    <p className="mt-1">
                      {apiUnavailable
                        ? t("hours.payroll_basis.api_unavailable_body")
                        : t("hours.payroll_basis.empty_body")}
                    </p>
                  </div>
                ) : (
                  employees.map((row) => {
                    const hasUnapproved = Boolean(row.has_unapproved);
                    return (
                      <div
                        key={row.id || row.user_id}
                        className={`rounded-xl border p-3 ${
                          hasUnapproved
                            ? "border-red-200 bg-red-50/60"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-slate-900">
                              {employeeName(row)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {employeeDepartment(row)}
                            </p>
                          </div>
                          {row.has_documentation ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                              <DocumentTextIcon className="h-3.5 w-3.5" />
                              {t("hours.payroll_basis.docs_yes")}
                            </span>
                          ) : null}
                        </div>

                        {hasUnapproved ? (
                          <p className="mt-2 text-xs font-semibold text-red-600">
                            {t("hours.payroll_basis.unapproved_badge", {
                              count: row.unapproved_count || 0,
                            })}
                          </p>
                        ) : null}

                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div className="rounded-lg bg-white/80 px-2.5 py-2 ring-1 ring-slate-100">
                            <div className="text-[11px] font-semibold uppercase text-slate-500">
                              {t("hours.payroll_basis.col_approved")}
                            </div>
                            <div className="font-semibold tabular-nums text-slate-900">
                              {formatHours(row.cached_approved_hours)}
                            </div>
                          </div>
                          <div
                            className={`rounded-lg px-2.5 py-2 ring-1 ${
                              hasUnapproved
                                ? "bg-red-100/80 ring-red-200"
                                : "bg-white/80 ring-slate-100"
                            }`}
                          >
                            <div
                              className={`text-[11px] font-semibold uppercase ${
                                hasUnapproved ? "text-red-700" : "text-slate-500"
                              }`}
                            >
                              {t("hours.payroll_basis.col_unapproved")}
                            </div>
                            <div
                              className={`font-semibold tabular-nums ${
                                hasUnapproved ? "text-red-700" : "text-slate-400"
                              }`}
                            >
                              {formatHours(row.cached_unapproved_hours)}
                            </div>
                          </div>
                          <div className="rounded-lg bg-white/80 px-2.5 py-2 ring-1 ring-slate-100">
                            <div className="text-[11px] font-semibold uppercase text-slate-500">
                              {t("hours.payroll_basis.col_overtime")}
                            </div>
                            <div className="font-semibold tabular-nums">
                              {formatHours(row.cached_overtime_hours)}
                            </div>
                          </div>
                          <div className="rounded-lg bg-white/80 px-2.5 py-2 ring-1 ring-slate-100">
                            <div className="text-[11px] font-semibold uppercase text-slate-500">
                              {t("hours.payroll_basis.col_absence")}
                            </div>
                            <div className="font-semibold tabular-nums">
                              {formatHours(row.cached_absence_hours)}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          <span className="mb-1 block text-xs font-semibold text-slate-600">
                            {t("hours.payroll_basis.col_note")}
                          </span>
                          <PayrollBasisEmployeeNoteCell
                            row={row}
                            value={noteDrafts[row.user_id] ?? ""}
                            editable={editable}
                            saving={savingNoteUserId === row.user_id}
                            onChange={(html) =>
                              setNoteDrafts((prev) => ({
                                ...prev,
                                [row.user_id]: html,
                              }))
                            }
                            onSave={(html) => handleSaveNote(row, html)}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <p className="px-1 text-xs text-slate-500">
          {t("hours.payroll_basis.legend")}
        </p>
      </div>

      <PMDialog
        open={reopenDialogOpen}
        onClose={() => !isReopening && setReopenDialogOpen(false)}
        title="hours.payroll_basis.reopen_dialog_title"
        size="md"
        closeOnOutsideClick={!isReopening}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("hours.payroll_basis.reopen_dialog_body")}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setReopenDialogOpen(false)}
              disabled={isReopening}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              {t("hours.payroll_basis.reopen_dialog_cancel")}
            </button>
            <button
              type="button"
              onClick={handleReopen}
              disabled={isReopening}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              <LockOpenIcon className="h-4 w-4" />
              {isReopening
                ? t("hours.payroll_basis.reopen_running")
                : t("hours.payroll_basis.reopen_dialog_confirm")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        title="hours.payroll_basis.export_dialog_title"
        size="md"
        closeOnOutsideClick
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("hours.payroll_basis.export_dialog_body")}
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => runExport("finalize_and_export")}
              disabled={exportState.active}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.55)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
              style={
                exportState.active
                  ? undefined
                  : { background: "linear-gradient(135deg,#1463C7,#2D8BE8)" }
              }
            >
              <LockClosedIcon className="h-4 w-4" />
              {t("hours.payroll_basis.export_dialog_finalize")}
            </button>
            <p className="px-1 text-xs text-slate-500">
              {t("hours.payroll_basis.export_dialog_finalize_hint")}
            </p>
            <button
              type="button"
              onClick={() => runExport("export_draft")}
              disabled={exportState.active}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              {t("hours.payroll_basis.export_dialog_draft")}
            </button>
            <p className="px-1 text-xs text-slate-500">
              {t("hours.payroll_basis.export_dialog_draft_hint")}
            </p>
          </div>
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={() => setExportDialogOpen(false)}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              {t("hours.payroll_basis.export_dialog_cancel")}
            </button>
          </div>
        </div>
      </PMDialog>
    </DashboardHeader>
  );
};
